import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import MasterPage from "./master-page";
import { getSessions } from "../actions/portal.action";
import { postTimeZone } from "../actions/account.action";
import storageService from "../services/storage-service";
import notification from "../services/notification-service";
import localizationService from "../services/localization-service";
import config from "../config";
import MoonLoader from "react-spinners/MoonLoader";
import ModalSessionIdentity from "../components/modals/modal-session-identity";

export const DashboardPage = () => {
  const [currentTimeZone, setCurrentTimeZone] = useState(null);
  const [timeZoneSelectedId, setTimeZoneSelectedId] = useState(null);
  const [timeZoneSet, setTimeZoneSet] = useState(false);
  const [expandedWeek, setExpandedWeek] = useState(0);

  const dispatch = useDispatch();
  const weeks = useSelector((state) => state.portal.sessions ?? []);
  const selectedlang = useSelector((state) => state.account.lang);
  const timeZones = useSelector((state) => state.account.timeZones);
  const sessionsLoading = useSelector((state) => state.portal.sessionsLoading);
  const [showTimeZoneValidationMsg, setshowTimeZoneValidationMsg] =
    useState(false);
  const [identityModalOpen, setIdentityModalOpen] = useState(false);
  const [pendingZoomUrl, setPendingZoomUrl] = useState(null);

  useEffect(() => {
    dispatch(getSessions(selectedlang));
  }, []);

  useEffect(() => {
    var ctz = storageService.getStorageItem(config.TIMEZONE);
    if (ctz != null) {
      setCurrentTimeZone(ctz);
      setTimeZoneSet(true);
      setTimeZoneSelectedId(ctz);
    }
  }, [currentTimeZone]);

  const changeDropDown = (timeZoneId) => {
    setTimeZoneSelectedId(timeZoneId);
    setshowTimeZoneValidationMsg(false);
  };

  const clickUpdateTimeZone = () => {
    if (timeZoneSelectedId == null) {
      setshowTimeZoneValidationMsg(true);
    } else {
      dispatch(postTimeZone(timeZoneSelectedId))
        .then(function (res) {
          setCurrentTimeZone(timeZoneSelectedId);
          setTimeZoneSelectedId(timeZoneSelectedId);
          notification.success(
            localizationService.getLocalizationValue(
              "timezoneUpdatedSuccess",
              selectedlang
            )
          );
          dispatch(getSessions(selectedlang));
        })
        .catch(function (res) {});
    }
  };

  const onClick = (item) => {
    setPendingZoomUrl(item.ZoomUrl);
    setIdentityModalOpen(true);
  };

  const handleIdentitySuccess = () => {
    setIdentityModalOpen(false);
    setPendingZoomUrl(null);
  };

  const handleIdentityClose = () => {
    setIdentityModalOpen(false);
    setPendingZoomUrl(null);
  };

  const toggleWeek = (index) => {
    setExpandedWeek(expandedWeek === index ? -1 : index);
  };

  return (
    <MasterPage>
      <div className="space-y-6">
        {/* Loading Overlay */}
        {sessionsLoading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-300">
            <MoonLoader
              color="#9aa82d"
              size={80}
              loading={sessionsLoading}
              aria-label="Loading Spinner"
            />
          </div>
        )}

        {/* Time Zone Section */}
        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-8 mb-8 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#98a62d] bg-opacity-10 flex items-center justify-center">
                <i className="fas fa-clock text-[#98a62d] text-base sm:text-base md:text-lg"></i>
              </div>
              <p className="text-gray-800 font-medium text-base sm:text-base md:text-base">
                {currentTimeZone !== null
                  ? localizationService.getLocalizationValue(
                      "currentTimeZone",
                      selectedlang
                    ) +
                    ": " +
                    timeZones.find((x) => x.id == currentTimeZone).name
                  : localizationService.getLocalizationValue(
                      "timeZoneNotSet",
                      selectedlang
                    )}
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <select
                className="block w-full pl-[7px] sm:pl-3 sm:pr-0 py-2 sm:py-2.5 text-base sm:text-base border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#98a62d] focus:border-[#98a62d] rounded-lg shadow-sm"
                value={timeZoneSelectedId || ""}
                onChange={(e) => changeDropDown(e.target.value)}
              >
                <option value="">
                  {localizationService.getLocalizationValue(
                    "selectTimeZone",
                    selectedlang
                  )}
                </option>
                {timeZones.map((tz) => (
                  <option key={tz.id} value={tz.id}>
                    {tz.name}
                  </option>
                ))}
              </select>
              <button
                onClick={clickUpdateTimeZone}
                className="inline-flex items-center px-4 py-2 sm:px-4 sm:py-2.5 border border-transparent text-base sm:text-base font-medium rounded-md text-white bg-[#9aa82d] hover:bg-[#7d8a1f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#98a62d] shadow-sm transition-colors duration-200"
              >
                {localizationService.getLocalizationValue(
                  "updateTimeZone",
                  selectedlang
                )}
              </button>
            </div>
          </div>
          {showTimeZoneValidationMsg && (
            <p className="mt-3 text-base sm:text-base text-red-600 bg-red-50 p-2 rounded-md">
              {localizationService.getLocalizationValue(
                "timeZoneValidationMsg",
                selectedlang
              )}
            </p>
          )}
        </div>

        {/* Sessions List - Desktop version (hidden on mobile) */}
        {weeks.length === 0 ? (
          <div className="hidden sm:block bg-white rounded-2xl shadow-xl p-4 sm:p-8">
            <div className="flex items-center p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
              <div className="flex-shrink-0">
                <i className="fas fa-exclamation-triangle text-yellow-400"></i>
              </div>
              <div className="ml-3">
                <p className="text-xs sm:text-sm text-yellow-700">
                  {localizationService.getLocalizationValue(
                    "noOnlineSession",
                    selectedlang
                  )}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Mobile version (no white background container) */}
            <div className="sm:hidden">
              <div className="mb-4 border-b border-gray-200 pb-3">
                <h2 className="text-lg font-semibold text-gray-800 uppercase">
                  {localizationService.getLocalizationValue(
                    "sessionHeading",
                    selectedlang
                  )}
                </h2>
              </div>
              <div
                className="space-y-3"
                style={timeZoneSet === false ? { display: "none" } : {}}
              >
                {weeks.map((w, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200"
                  >
                    <div className="border-b border-gray-200">
                      <button
                        className={`w-full px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between text-left focus:outline-none transition-colors duration-150 ${
                          expandedWeek === index
                            ? "bg-[#98a62d] text-white hover:bg-[#7d8a1f]"
                            : "hover:bg-gray-50 text-gray-900"
                        }`}
                        onClick={() => toggleWeek(index)}
                        aria-expanded={expandedWeek === index}
                      >
                        <h3
                          className={`text-base sm:text-lg font-medium ${
                            expandedWeek === index
                              ? "text-white"
                              : "text-gray-900"
                          }`}
                        >
                          {w.WeekTitle}
                        </h3>
                        <i
                          className={`fas fa-chevron-down transform transition-transform duration-200 ${
                            expandedWeek === index
                              ? "rotate-180 text-white"
                              : "text-gray-400"
                          }`}
                        ></i>
                      </button>
                    </div>

                    <div
                      className={`transition-all duration-200 ease-in-out ${
                        expandedWeek === index
                          ? "max-h-[2000px] opacity-100"
                          : "max-h-0 opacity-0"
                      } overflow-hidden`}
                    >
                      <div className="divide-y divide-gray-200">
                        {w.Dates.map((s, sessionNum) => (
                          <div
                            key={sessionNum}
                            className="p-3 sm:p-6 hover:bg-gray-50 transition-colors duration-150"
                          >
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                              <div className="flex-1">
                                <div className="flex flex-row items-start">
                                  <div className="flex-shrink-0 order-2 md:order-1 md:ml-0 mr-0 md:mr-4">
                                    <div
                                      className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center ${
                                        s.IsDisabled
                                          ? "bg-red-100"
                                          : "bg-green-100"
                                      }`}
                                    >
                                      <i
                                        className={`fas ${
                                          s.IsDisabled
                                            ? "fa-times text-red-500 text-xl sm:text-2xl"
                                            : "fa-check text-green-500 text-xl sm:text-2xl"
                                        }`}
                                      ></i>
                                    </div>
                                  </div>
                                  <div className="flex-1 order-1 md:order-2">
                                    <h4 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-2">
                                      {localizationService.getLocalizationValue(
                                        "sessionTitle",
                                        selectedlang
                                      )}
                                      :{" "}
                                      <span className="text-[#98a62d] ml-1">
                                        {s.SessionName}
                                      </span>
                                    </h4>
                                    <div className="space-y-3 sm:space-y-4 text-sm sm:text-sm md:text-base">
                                      <div className="flex items-center space-x-2">
                                        <span className="font-bold text-gray-800">
                                          {localizationService.getLocalizationValue(
                                            "counsellor",
                                            selectedlang
                                          )}
                                          :
                                        </span>
                                        <span className="font-medium text-gray-700">
                                          {s.Councellor}
                                        </span>
                                      </div>
                                      <div className="flex items-center space-x-2">
                                        <span className="font-bold text-gray-800">
                                          {localizationService.getLocalizationValue(
                                            "date",
                                            selectedlang
                                          )}
                                          :
                                        </span>
                                        <span className="font-medium text-gray-700">
                                          {s.Date}
                                        </span>
                                      </div>
                                      <div className="flex items-center space-x-2">
                                        <span className="font-bold text-gray-800">
                                          {localizationService.getLocalizationValue(
                                            "time",
                                            selectedlang
                                          )}
                                          :
                                        </span>
                                        <span className="font-medium text-gray-700">
                                          {s.Time}
                                        </span>
                                      </div>
                                      {s.ZoomUrl && (
                                        <div className="flex items-center space-x-2">
                                          <span className="font-bold text-gray-800">
                                            Passcode:
                                          </span>
                                          <span className="font-semibold text-[#98a62d]">
                                            {s.ZoomPassword}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="md:mt-0 md:ml-6 flex flex-col mt-4">
                                {s.IsDisabled ? (
                                  <div className="text-sm sm:text-md font-bold text-red-500 mb-4">
                                    {localizationService.getLocalizationValue(
                                      "sessionCancelled",
                                      selectedlang
                                    )}
                                  </div>
                                ) : (
                                  s.ZoomUrl && (
                                    <button
                                      onClick={() => onClick(s)}
                                      className="inline-flex items-center justify-center px-3 py-2 sm:px-4 sm:py-2.5 md:px-6 md:py-3 border border-transparent text-xs sm:text-sm md:text-base font-bold rounded-md text-white bg-[#9aa82d] hover:bg-[#7d8a1f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#9aa82d] transition-colors duration-200 shadow-sm"
                                    >
                                      <i className="fas fa-video mr-2"></i>
                                      {localizationService.getLocalizationValue(
                                        "joinSession",
                                        selectedlang
                                      )}
                                    </button>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Desktop version (with white background container) */}
            <div className="hidden sm:block bg-white rounded-2xl shadow-xl p-4 sm:p-8">
              <div className="mb-4 border-b border-gray-200 pb-3">
                <h2 className="text-xl font-semibold text-gray-800 uppercase">
                  {localizationService.getLocalizationValue(
                    "sessionHeading",
                    selectedlang
                  )}
                </h2>
              </div>
              <div
                className="space-y-5"
                style={timeZoneSet === false ? { display: "none" } : {}}
              >
                {weeks.map((w, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-200"
                  >
                    <div className="border-b border-gray-200">
                      <button
                        className={`w-full px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between text-left focus:outline-none transition-colors duration-150 ${
                          expandedWeek === index
                            ? "bg-[#98a62d] text-white hover:bg-[#7d8a1f]"
                            : "hover:bg-gray-50 text-gray-900"
                        }`}
                        onClick={() => toggleWeek(index)}
                        aria-expanded={expandedWeek === index}
                      >
                        <h3
                          className={`text-base sm:text-lg font-medium ${
                            expandedWeek === index
                              ? "text-white"
                              : "text-gray-900"
                          }`}
                        >
                          {w.WeekTitle}
                        </h3>
                        <i
                          className={`fas fa-chevron-down transform transition-transform duration-200 ${
                            expandedWeek === index
                              ? "rotate-180 text-white"
                              : "text-gray-400"
                          }`}
                        ></i>
                      </button>
                    </div>

                    <div
                      className={`transition-all duration-200 ease-in-out ${
                        expandedWeek === index
                          ? "max-h-[2000px] opacity-100"
                          : "max-h-0 opacity-0"
                      } overflow-hidden`}
                    >
                      <div className="divide-y divide-gray-200">
                        {w.Dates.map((s, sessionNum) => (
                          <div
                            key={sessionNum}
                            className="p-4 sm:p-6 hover:bg-gray-50 transition-colors duration-150"
                          >
                            <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                              <div className="flex-1">
                                <div className="flex flex-row items-start space-x-4">
                                  <div className="flex-shrink-0 order-2 md:order-1 md:ml-0 mr-0 md:mr-4">
                                    <div
                                      className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center ${
                                        s.IsDisabled
                                          ? "bg-red-100"
                                          : "bg-green-100"
                                      }`}
                                    >
                                      <i
                                        className={`fas ${
                                          s.IsDisabled
                                            ? "fa-times text-red-500 text-xl sm:text-2xl"
                                            : "fa-check text-green-500 text-xl sm:text-2xl"
                                        }`}
                                      ></i>
                                    </div>
                                  </div>
                                  <div className="flex-1 order-1 md:order-2">
                                    <h4 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 mb-4">
                                      {localizationService.getLocalizationValue(
                                        "sessionTitle",
                                        selectedlang
                                      )}
                                      :{" "}
                                      <span className="text-[#98a62d] ml-1">
                                        {s.SessionName}
                                      </span>
                                    </h4>
                                    <div className="space-y-3 sm:space-y-4 text-sm sm:text-sm md:text-base">
                                      <div className="flex items-center space-x-2">
                                        <span className="font-bold text-gray-800">
                                          {localizationService.getLocalizationValue(
                                            "counsellor",
                                            selectedlang
                                          )}
                                          :
                                        </span>
                                        <span className="font-medium text-gray-700">
                                          {s.Councellor}
                                        </span>
                                      </div>
                                      <div className="flex items-center space-x-2">
                                        <span className="font-bold text-gray-800">
                                          {localizationService.getLocalizationValue(
                                            "date",
                                            selectedlang
                                          )}
                                          :
                                        </span>
                                        <span className="font-medium text-gray-700">
                                          {s.Date}
                                        </span>
                                      </div>
                                      <div className="flex items-center space-x-2">
                                        <span className="font-bold text-gray-800">
                                          {localizationService.getLocalizationValue(
                                            "time",
                                            selectedlang
                                          )}
                                          :
                                        </span>
                                        <span className="font-medium text-gray-700">
                                          {s.Time}
                                        </span>
                                      </div>
                                      {s.ZoomUrl && (
                                        <div className="flex items-center space-x-2">
                                          <span className="font-bold text-gray-800">
                                            Passcode:
                                          </span>
                                          <span className="font-semibold text-[#98a62d]">
                                            {s.ZoomPassword}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="md:mt-0 md:ml-6 flex flex-col items-end mt-4">
                                {s.IsDisabled ? (
                                  <div className="text-sm sm:text-md font-bold text-red-500 mb-4">
                                    {localizationService.getLocalizationValue(
                                      "sessionCancelled",
                                      selectedlang
                                    )}
                                  </div>
                                ) : (
                                  s.ZoomUrl && (
                                    <button
                                      onClick={() => onClick(s)}
                                      className="inline-flex items-center px-3 py-2 sm:px-4 sm:py-2.5 md:px-6 md:py-3 border border-transparent text-xs sm:text-sm md:text-base font-bold rounded-md text-white bg-[#9aa82d] hover:bg-[#7d8a1f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#9aa82d] transition-colors duration-200 shadow-sm"
                                    >
                                      <i className="fas fa-video mr-2"></i>
                                      {localizationService.getLocalizationValue(
                                        "joinSession",
                                        selectedlang
                                      )}
                                    </button>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      <ModalSessionIdentity
        isOpen={identityModalOpen}
        onClose={handleIdentityClose}
        onSuccess={handleIdentitySuccess}
        redirectUrl={pendingZoomUrl}
      />
    </MasterPage>
  );
};
